import { configurePipelineClient } from "./pipeline";
import { configureDb, getItem, updateItem, Tables } from "./db";
import { configureCodeCommit } from "./commits";
import type { AuthSession, PortalRole } from "../types";

const USERS_TABLE = Tables.USERS;
const STORAGE_KEY = "devportal-auth-session";
const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL || "https://csjto20hja.execute-api.us-east-1.amazonaws.com";

/**
 * Bootstrap credentials — baked into the build via env vars.
 * This user can ONLY read the pos-portal-users table.
 * Used to authenticate logins from any device.
 */
const BOOTSTRAP_CREDENTIALS = {
  accessKeyId: import.meta.env.VITE_BOOTSTRAP_ACCESS_KEY_ID || "",
  secretAccessKey: import.meta.env.VITE_BOOTSTRAP_SECRET_ACCESS_KEY || "",
};

let bootstrapped = false;

/** Ensure DB is configured with at least bootstrap credentials (for non-auth operations) */
export function ensureBootstrap() {
  if (!bootstrapped && BOOTSTRAP_CREDENTIALS.accessKeyId) {
    configureDb(BOOTSTRAP_CREDENTIALS);
    bootstrapped = true;
  }
}

export function isBootstrapped(): boolean {
  return bootstrapped || !!BOOTSTRAP_CREDENTIALS.accessKeyId;
}

/**
 * Manual bootstrap — for first-time setup when env vars aren't set.
 */
export function bootstrap(credentials: {
  accessKeyId: string;
  secretAccessKey: string;
}) {
  configureDb(credentials);
  bootstrapped = true;
}

export interface PortalUser {
  username: string;
  passwordHash: string;
  role: PortalRole;
  displayName: string;
  email: string;
  accessKeyId: string;
  secretAccessKey: string;
  createdAt: string;
  lastLogin?: string;
  mustChangePassword?: boolean; // true = force password change on next login
}

/**
 * Simple hash for passwords (browser-compatible).
 * In production you'd use bcrypt via a backend Lambda,
 * but for a Tooling portal this is adequate.
 */
async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Login with username and password via the Auth API.
 * No DynamoDB credentials needed in the browser.
 */
export async function login(username: string, password: string): Promise<AuthSession> {
  const resp = await fetch(`${AUTH_API_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data.error || "Authentication failed");
  }

  // Configure SDK clients with the user's IAM credentials
  const credentials = {
    accessKeyId: data.accessKeyId,
    secretAccessKey: data.secretAccessKey,
  };

  configurePipelineClient(credentials);
  configureDb(credentials);
  configureCodeCommit(credentials);
  bootstrapped = true;

  // Build session
  const session: AuthSession = {
    username: data.username,
    displayName: data.displayName,
    role: data.role,
    accessKeyId: data.accessKeyId,
    secretAccessKey: data.secretAccessKey,
    mustChangePassword: data.mustChangePassword,
  };

  // Persist session
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));

  return session;
}

/** Restore session from localStorage */
export function restoreSession(): AuthSession | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;

  try {
    const session: AuthSession = JSON.parse(stored);

    // Reconfigure SDK clients
    const credentials = {
      accessKeyId: session.accessKeyId,
      secretAccessKey: session.secretAccessKey,
    };
    configurePipelineClient(credentials);
    configureDb(credentials);
    configureCodeCommit(credentials);
    bootstrapped = true;

    return session;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/** Clear session */
export function logout(): void {
  localStorage.removeItem(STORAGE_KEY);
  bootstrapped = false;
}

// ============================
// User Management (Admin only)
// ============================

/** Create a new portal user via Auth API (server-side IAM operations) */
export async function createUser(
  data: {
    username: string;
    password?: string;
    role: PortalRole;
    displayName: string;
    email: string;
    sendEmail?: boolean;
  },
  onProgress?: (step: string, status: "pending" | "active" | "done" | "error") => void
): Promise<PortalUser> {
  const progress = onProgress || (() => {});

  progress("Creating user account", "active");

  const resp = await fetch(`${AUTH_API_URL}/auth/create-user`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      username: data.username,
      displayName: data.displayName,
      email: data.email,
      role: data.role,
      password: data.password || undefined,
    }),
  });

  const result = await resp.json();

  if (!resp.ok) {
    progress("Creating user account", "error");
    throw new Error(result.error || "Failed to create user");
  }

  progress("Creating user account", "done");

  // If email sending requested and we have a temp password
  if (data.sendEmail && data.email && result.tempPassword) {
    progress("Sending welcome email", "active");
    try {
      await sendTempPasswordEmail(data.email, data.username, result.tempPassword);
      progress("Sending welcome email", "done");
    } catch {
      progress("Sending welcome email", "error");
    }
  }

  progress("Ready", "done");

  return {
    username: data.username,
    displayName: data.displayName,
    email: data.email,
    role: data.role,
    passwordHash: "",
    accessKeyId: "",
    secretAccessKey: "",
    createdAt: new Date().toISOString(),
    mustChangePassword: !data.password,
  };
}

/** Get all portal users via Auth API */
export async function getAllUsers(): Promise<Omit<PortalUser, "passwordHash" | "secretAccessKey">[]> {
  const resp = await fetch(`${AUTH_API_URL}/auth/list-users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(data.error || "Failed to list users");
  return data.users;
}

/** Update a user's role via Auth API */
export async function updateUserRole(username: string, role: PortalRole): Promise<void> {
  const resp = await fetch(`${AUTH_API_URL}/auth/update-role`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, role }),
  });
  if (!resp.ok) {
    const data = await resp.json();
    throw new Error(data.error || "Failed to update role");
  }
}

/** Update a user's password */
export async function updateUserPassword(username: string, newPassword: string): Promise<void> {
  const hash = await hashPassword(newPassword);
  await updateItem(USERS_TABLE, { username }, { passwordHash: hash });
}

/** Update a user's IAM credentials */
export async function updateUserCredentials(
  username: string,
  accessKeyId: string,
  secretAccessKey: string
): Promise<void> {
  await updateItem(USERS_TABLE, { username }, { accessKeyId, secretAccessKey });
}

/** Delete a portal user via Auth API */
export async function deleteUser(username: string): Promise<void> {
  const resp = await fetch(`${AUTH_API_URL}/auth/delete-user`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });
  if (!resp.ok) {
    const data = await resp.json();
    throw new Error(data.error || "Failed to delete user");
  }
}

/** Check if a user's session is still valid and return current role */
export async function isSessionValid(username: string): Promise<{ valid: boolean; role?: PortalRole; displayName?: string }> {
  try {
    const resp = await fetch(`${AUTH_API_URL}/auth/validate-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    const data = await resp.json();
    return { valid: data.valid === true, role: data.role, displayName: data.displayName };
  } catch {
    // Network error — assume valid to avoid logout on connectivity issues
    return { valid: true };
  }
}

/** Check if user must change password */
export async function mustChangePassword(username: string): Promise<boolean> {
  const user = await getItem(USERS_TABLE, { username }) as PortalUser | undefined;
  return user?.mustChangePassword === true;
}

/**
 * Reset a user's password via Auth API.
 * Returns the temp password so it can be shown/emailed.
 */
export async function resetUserPassword(username: string): Promise<string> {
  const resp = await fetch(`${AUTH_API_URL}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(data.error || "Failed to reset password");
  return data.tempPassword;
}

/**
 * Send temp password via SES email.
 */
export async function sendTempPasswordEmail(
  email: string,
  username: string,
  tempPassword: string,
  senderEmail: string = "noreply@devportal.rmlittlebits.com"
): Promise<void> {
  const { SESClient, SendEmailCommand } = await import("@aws-sdk/client-ses");
  const ses = new SESClient({ region: "us-east-1" });

  await ses.send(new SendEmailCommand({
    Source: senderEmail,
    Destination: { ToAddresses: [email] },
    Message: {
      Subject: { Data: "BSDP — Your Account Credentials" },
      Body: {
        Html: {
          Data: `
            <h2>Welcome to BSDP</h2>
            <p>An account has been created for you on the Business System Development Portal.</p>
            <table style="margin: 1rem 0; font-family: monospace;">
              <tr><td style="padding: 0.25rem 1rem; font-weight: bold;">Username:</td><td>${username}</td></tr>
              <tr><td style="padding: 0.25rem 1rem; font-weight: bold;">Temporary Password:</td><td>${tempPassword}</td></tr>
            </table>
            <p><strong>You will be required to change your password on first login.</strong></p>
            <p style="color: #666; font-size: 0.85rem;">If you did not expect this email, please ignore it.</p>
          `,
        },
        Text: {
          Data: `BSDP Account\n\nUsername: ${username}\nTemporary Password: ${tempPassword}\n\nYou will be required to change your password on first login.`,
        },
      },
    },
  }));
}

/** Change password via Auth API and clear the mustChangePassword flag in localStorage */
export async function changePassword(username: string, newPassword: string): Promise<void> {
  const resp = await fetch(`${AUTH_API_URL}/auth/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, newPassword }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data.error || "Failed to change password");
  }

  // Update localStorage session to clear mustChangePassword
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const session = JSON.parse(stored);
    session.mustChangePassword = false;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }
}

// ============================
// Permissions
// ============================

export type Permission =
  | "pipeline:view"
  | "pipeline:trigger"
  | "pipeline:stop"
  | "pipeline:approve-frontend"
  | "pipeline:approve-backend"
  | "pipeline:rollback"
  | "pipeline:gates"
  | "versions:create"
  | "versions:promote-dev"
  | "versions:promote-beta"
  | "versions:promote-prod"
  | "features:manage"
  | "bugs:manage"
  | "admin:users";

const ROLE_PERMISSIONS: Record<PortalRole, Permission[]> = {
  admin: [
    "pipeline:view",
    "pipeline:trigger",
    "pipeline:stop",
    "pipeline:approve-frontend",
    "pipeline:approve-backend",
    "pipeline:rollback",
    "pipeline:gates",
    "versions:create",
    "versions:promote-dev",
    "versions:promote-beta",
    "versions:promote-prod",
    "features:manage",
    "bugs:manage",
    "admin:users",
  ],
  authorizer: [
    "pipeline:view",
    "pipeline:approve-frontend",
    "pipeline:approve-backend",
    "pipeline:rollback",
    "versions:create",
    "versions:promote-dev",
    "versions:promote-beta",
    "versions:promote-prod",
    "features:manage",
    "bugs:manage",
  ],
  user: [
    "pipeline:view",
    "pipeline:approve-frontend",
    "versions:create",
    "versions:promote-dev",
    "versions:promote-beta",
    "features:manage",
    "bugs:manage",
  ],
};

/** Check if a role has a specific permission */
export function hasPermission(role: PortalRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Get all permissions for a role */
export function getPermissions(role: PortalRole): Permission[] {
  return ROLE_PERMISSIONS[role];
}
